import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "../lib/useLanguage";
import { getUserById } from "../lib/mockData";
import { toast } from "sonner";

const FORMATS = ["En ligne", "Lieu public", "Groupe"];
const DURATIONS = ["20 min", "45 min", "1h"];

export default function MessageRequestForm({ card, onSent, onClose }) {
  const t = useT();
  const [msg, setMsg] = useState("");
  const [format, setFormat] = useState("");
  const [duration, setDuration] = useState("");
  const user = card ? getUserById(card.userId) : null;

  if (!card) return null;

  function handleSend() {
    if (!msg.trim()) { toast.error("Écris un message."); return; }
    toast.success(t("request_sent") + " ✓");
    onSent?.();
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end justify-center"
        style={{ backgroundColor: "rgba(16,24,40,0.6)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 200, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 280 }}
          onClick={e => e.stopPropagation()}
          className="w-full max-w-lg rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto"
          style={{ backgroundColor: "#FFF4E8" }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-muted-foreground">{t("req_form_title")}</p>
              <h3 className="font-extrabold text-base text-foreground">{card.title}</h3>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          {/* To */}
          {user && (
            <div className="flex items-center gap-2.5 bg-white rounded-xl p-3 mb-4 border border-border">
              <img src={user.photo} alt="" className="h-8 w-8 rounded-xl object-cover" />
              <div>
                <p className="text-sm font-semibold">{user.firstName}</p>
                <p className="text-xs text-muted-foreground">{user.zone}</p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex gap-2">
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="flex-1 rounded-xl bg-white h-10 text-sm">
                  <SelectValue placeholder={t("req_format")} />
                </SelectTrigger>
                <SelectContent>{FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="flex-1 rounded-xl bg-white h-10 text-sm">
                  <SelectValue placeholder={t("req_duration")} />
                </SelectTrigger>
                <SelectContent>{DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Textarea
                value={msg}
                onChange={e => setMsg(e.target.value)}
                maxLength={250}
                placeholder={t("msg_placeholder")}
                rows={3}
                className="rounded-xl bg-white resize-none text-sm"
              />
              <p className="text-xs text-muted-foreground text-right mt-1">{msg.length}/250</p>
            </div>

            <p className="text-xs text-muted-foreground px-1">
              {t("msg_one_request")}
            </p>

            <Button onClick={handleSend} className="w-full rounded-xl font-bold py-5" style={{ backgroundColor: "#FF6B4A" }}>
              <Send className="h-4 w-4 mr-2" />
              {t("req_send")}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}